import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from "../../utils/Enums";
import { apiClient } from "../../utils/ApiClient";
import { NewSMSSenderFormProps } from "../../components/smsSenders/NewSMSSenderScreen";
import { ModifySMSSenderFormProps } from "../../components/smsSenders/ModifySMSSenderScreen";
import SMSSender from "../../models/SMSSender";

interface SMSSendersState {
    SMSSenders: SMSSender[];
    currentSMSSender?: SMSSender | null;
    getSMSSendersStatus: RequestStatus;
    getCurrentSMSSender: RequestStatus;
    createSMSSenderStatus: RequestStatus;
    updateSMSSenderStatus: RequestStatus;
    deleteSMSSenderStatus: RequestStatus;
    errors: string[];
}

const initialState: SMSSendersState = {
    SMSSenders: [],
    currentSMSSender: null,
    getSMSSendersStatus: RequestStatus.Idle,
    getCurrentSMSSender: RequestStatus.Idle,
    createSMSSenderStatus: RequestStatus.Idle,
    updateSMSSenderStatus: RequestStatus.Idle,
    deleteSMSSenderStatus: RequestStatus.Idle,
    errors: [],
};

export const getSMSSenders = createAsyncThunk(
    'SMSSenders/getSMSSenders',
    async (_, { rejectWithValue }) => {
        const response = await apiClient.get('sms-senders');
        return response.data;
    }
);

export const createSMSSender = createAsyncThunk(
    'SMSSenders/createSMSSender',
    async (data: NewSMSSenderFormProps, { rejectWithValue }) => {
        const response = await apiClient.post('sms-senders', data);
        return response.data;
    }
);

export const getCurrentSMSSender = createAsyncThunk(
    'SMSSenders/getCurrentSMSSender',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.get(`sms-senders/${id}`);
        return response.data;
    }
);

export const updateSMSSender = createAsyncThunk(
    'SMSSenders/updateSMSSender',
    async ({ id, data }: { id: string | undefined, data: ModifySMSSenderFormProps }, { rejectWithValue }) => {
        const response = await apiClient.put(`sms-senders/${id}`, data);
        return response.data;
    }
);

export const deleteSMSSender = createAsyncThunk(
    'SMSSenders/deleteSMSSender',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.delete(`sms-senders/${id}`);
        return response.data;
    }
);

const SMSSendersSlice = createSlice({
    name: 'SMSSenders',
    initialState,
    reducers: {
        resetCreateSMSSenderStatus: (state) => {
            state.createSMSSenderStatus = RequestStatus.Idle;
            state.currentSMSSender = null;
        },
        resetUpdateSMSSenderStatus: (state) => {
            state.updateSMSSenderStatus = RequestStatus.Idle;
            state.currentSMSSender = null;
        },
        resetDeleteSMSSenderStatus: (state) => {
            state.deleteSMSSenderStatus = RequestStatus.Idle;
            state.currentSMSSender = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSMSSenders.pending, (state, action) => {
                state.getSMSSendersStatus = RequestStatus.Loading;
            })
            .addCase(getSMSSenders.fulfilled, (state, action) => {
                state.getSMSSendersStatus = RequestStatus.Success;
                state.SMSSenders = action.payload;
            })
            .addCase(getSMSSenders.rejected, (state, action) => {
                state.getSMSSendersStatus = RequestStatus.Failed;
            })

            .addCase(createSMSSender.pending, (state, action) => {
                state.createSMSSenderStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createSMSSender.fulfilled, (state, action) => {
                state.createSMSSenderStatus = RequestStatus.Success;
            })
            .addCase(createSMSSender.rejected, (state, action) => {
                state.createSMSSenderStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateSMSSender.pending, (state, action) => {
                state.updateSMSSenderStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateSMSSender.fulfilled, (state, action) => {
                state.updateSMSSenderStatus = RequestStatus.Success;
            })
            .addCase(updateSMSSender.rejected, (state, action) => {
                state.updateSMSSenderStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteSMSSender.pending, (state, action) => {
                state.deleteSMSSenderStatus = RequestStatus.Loading;
            })
            .addCase(deleteSMSSender.fulfilled, (state, action) => {
                state.deleteSMSSenderStatus = RequestStatus.Success;
            })
            .addCase(deleteSMSSender.rejected, (state, action) => {
                state.deleteSMSSenderStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentSMSSender.pending, (state, action) => {
                state.getCurrentSMSSender = RequestStatus.Loading;
            })
            .addCase(getCurrentSMSSender.fulfilled, (state, action) => {
                state.getCurrentSMSSender = RequestStatus.Success;
                state.currentSMSSender = action.payload;
            })
            .addCase(getCurrentSMSSender.rejected, (state, action) => {
                state.getCurrentSMSSender = RequestStatus.Failed;
            });
    },
});

export const {
    resetCreateSMSSenderStatus,
    resetUpdateSMSSenderStatus,
    resetDeleteSMSSenderStatus
} = SMSSendersSlice.actions;

export default SMSSendersSlice.reducer;